type Direction = 'previous' | 'next';

/** @internal */
export interface VirtualFocusItem {
  id?: string;
  type?: 'suggestion' | 'recent-suggestion' | 'pinned-suggestion' | 'button';
}

function clampIndex(
  index: number,
  length: number,
  wrapAround: boolean,
): number {
  if (wrapAround) {
    if (index >= length) {
      return 0;
    } else if (index < 0) {
      return length - 1;
    }
  }
  return Math.max(0, Math.min(index, length - 1));
}

function getAllFocusableListItems(
  container: HTMLElement | null,
): Array<HTMLElement | undefined> | null {
  if (!container) {
    return null;
  }

  return Array.from(
    container.querySelectorAll<HTMLElement>(
      '[data-strato-filter-field-virtual-focus-id]',
    ),
  );
}

/** @internal */
export function getCurrentFocusableListItemIndex(
  container: HTMLElement | null,
  currentSuggestionId?: string,
): number {
  if (!container || !currentSuggestionId) {
    return -1;
  }

  const currentSuggestion = getCurrentFocusableListItem(
    container,
    currentSuggestionId,
  );
  const allFocusableListItems = getAllFocusableListItems(container);

  if (currentSuggestion && allFocusableListItems) {
    return allFocusableListItems.indexOf(currentSuggestion);
  }

  return -1;
}

/** @internal */
export function getCurrentFocusableListItemByIndex(
  container: HTMLElement | null,
  index: number,
): HTMLElement | null {
  if (!container) {
    return null;
  }

  const allFocusableListItems = getAllFocusableListItems(container);

  if (
    allFocusableListItems &&
    index >= 0 &&
    index < allFocusableListItems.length
  ) {
    return allFocusableListItems[index] ?? null;
  }

  return null;
}

/** @internal */
export function getCurrentFocusableListItem(
  container: HTMLElement | null,
  currentSuggestionId?: string,
): HTMLElement | null {
  if (
    !container ||
    !currentSuggestionId ||
    // Weird edge case handling as at least the JSDOM query selector will return
    // the entire parent if the selector contains a undefined or null string.
    currentSuggestionId === 'null' ||
    currentSuggestionId === 'undefined'
  ) {
    return null;
  }

  return container.querySelector<HTMLElement>(
    `[data-strato-filter-field-virtual-focus-id="${currentSuggestionId}"]`,
  );
}

/** @internal */
export function getFirstFocusableListItem(
  container: HTMLElement | null,
): HTMLElement | null {
  if (!container) {
    return null;
  }
  return container.querySelector<HTMLElement>(
    '[data-strato-filter-field-virtual-focus-id]',
  );
}

/** @internal */
export function getLastFocusableListItem(
  container: HTMLElement | null,
): HTMLElement | null {
  if (!container) {
    return null;
  }

  const allFocusableElements = Array.from(
    container.querySelectorAll<HTMLElement>(
      '[data-strato-filter-field-virtual-focus-id]',
    ),
  );

  return Array.from(allFocusableElements).at(-1) ?? null;
}

/**
 * Get the next suggestion in the given container element.
 * If direction is set to 'next', start at the first suggestion. Else, start at the last.
 * If a 'currentSuggestionId' is passed, start at the matching suggestion.
 * @internal
 */
export function getNextFocusableListItem(
  direction: Direction,
  container: HTMLElement | null,
  currentSuggestionId?: string,
  nthSibling: number = 1,
): HTMLElement | null {
  if (!container) {
    return null;
  }

  // Get the current and all eligable focusable list items.
  const currentSuggestion = getCurrentFocusableListItem(
    container,
    currentSuggestionId,
  );
  const allFocusableListItems = getAllFocusableListItems(container);

  if (currentSuggestion) {
    const currentIndex = allFocusableListItems?.indexOf(currentSuggestion);
    if (
      currentIndex !== undefined &&
      currentIndex > -1 &&
      allFocusableListItems
    ) {
      const modifier = direction === 'next' ? nthSibling : nthSibling * -1;
      const newIndex = clampIndex(
        currentIndex + modifier,
        allFocusableListItems.length,
        nthSibling === 1,
      );
      return allFocusableListItems[newIndex] ?? null;
    }
  } else if (direction === 'next') {
    return (
      allFocusableListItems?.at(
        // Count the nthSibling from the start depending on the step
        clampIndex(0 + (nthSibling - 1), allFocusableListItems.length, false),
      ) ?? null
    );
  } else if (direction === 'previous') {
    return (
      allFocusableListItems?.at(
        // Count the nthSibling from the end depending on the step
        clampIndex(nthSibling, allFocusableListItems.length, false) * -1,
      ) ?? null
    );
  }
  return null;
}
